<script>
  import { getContext, onMount } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import { fly } from "svelte/transition";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import Header from "@components/Header.svelte";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";

  import { projectListStore } from "@stores/damProjectsStore.js";
  import { useUrlParams } from "@stores/urlStore.js";
  import { applicationLimitationStore } from "@stores/applicationLimitationStore.js";
  import { MultiSelect, ToastNotification } from "carbon-components-svelte";

  import LimitationList from "../components/LimitationList.svelte";

  import { formatTimeDelta } from "lib/util/formatfunctions.js";
  import DataTableBlack from "@components/DataTableBlack.svelte";

  import ContentSwitcherNew from "../../../../lib/components/ContentSwitcher.svelte";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import {
    Button,
    InfoBlock,
    SaveStatus,
    SectionWrapper,
    Tag,
    Tooltip,
  } from "paxray-components-svelte";
  import EmptyStateText from "../components/EmptyStateText.svelte";

  export let isCompleted = false;

  const role = getContext("role");
  $: isAdmin = $role?.role === "admin";

  const { getProjectLimitations } = projectListStore;
  const urlParams = useUrlParams();
  const step = PROJECT_STAGES.SCOPING.steps.DEF_WHTLST;
  const stage = PROJECT_STAGES.SCOPING.id;
  const shareHeaderTooltip = "Share of the total across all teams, in percent.";

  let teamids = [];
  let toastMsg;
  let saveSuccessful;

  let selectedRowIds = [];
  let addedApplications = [];
  // let overviewkpipromise = getAppOverviewKPIs();
  let webapploading = true;
  let sortKey = "time";
  let sortDirection = "descending";
  let overview = [];
  let webapps = [];
  let isWebApp = 0;
  let appsPage = 1;
  let appsPageSize = 10;
  let webPage = 1;
  let webPageSize = 10;
  let desktopSearchValue = "";
  let webSearchValue = "";
  let message = "";
  let confirmmessage = "";
  let switcherOptions = ["Apps", "Websites"];
  let teams = [];
  let teamsweb = [];
  let originalOverview = [];
  let originalWebapps = [];
  let applicationLimitations = [];
  let firstTime = true;
  let overviewloading = true;
  let showWhitelistGuide = false;
  let whitelistGuideStep = 0;
  let whitelistGuideSteps = [];

  onMount(() =>
    registerViewTour(() => {
      if (tableLoading) return;
      const completed =
        isCompleted ||
        Boolean(
          document.querySelector(
            '[data-guide="complete-whitelist"] .completed-badge',
          ),
        );
      whitelistGuideSteps = [
        {
          target: '[data-guide="whitelist-overview"]',
          title: "Choose the scope of data collection",
          text: "Your whitelist defines which apps and websites are included in data collection during Discovery. Include the ones relevant to your project’s processes and tasks.",
        },
        {
          target: '[data-guide="detected-whitelist"]',
          highlightSelector: ".toolbar_content_switcher, .bx--search",
          title: "Find apps and websites",
          text: "Switch between Apps and Websites to review what was identified during Scoping. Use search to find specific entries, and check both lists before continuing.",
        },
        {
          target: '[data-guide="detected-whitelist"]',
          highlightSelector: ".team_selector, thead",
          title: "Review usage across teams",
          text: "Filter by team to see which apps and websites they use. Usage, Keystrokes and Mouse Clicks show each share of the total and help you understand usage patterns. Choose entries based on their relevance to your project, not just how frequently they are used.",
        },
        {
          target: '[data-guide="detected-whitelist"]',
          highlightSelector:
            ".bx--checkbox-label, .custom-table-toolbar-button",
          title: "Add your selection",
          text: completed
            ? "Before completion, you select the entries you want to include, then click “Add selected to whitelist”. Selecting a checkbox alone does not add an entry to the whitelist. This step is now complete and the whitelist is locked."
            : "Select the entries you want to include, then click “Add selected to whitelist”. Selecting a checkbox alone does not add an entry to the whitelist.",
        },
        {
          target: '[data-guide="whitelist-content"]',
          highlightSelector:
            '.whitelist-summary, [data-guide="whitelist-entries"]',
          title: "Check and refine your whitelist",
          text: completed
            ? "The summary shows how many apps and websites are included in data collection. Review the saved entries in the list below. This step is complete and the whitelist settings are locked."
            : "The summary shows how many apps and websites are included in data collection. Added entries are saved automatically. Review the list below, use the row actions to edit or delete entries, or choose “Add App or Website” to add an entry manually.",
        },
        {
          target:
            '[data-guide="complete-whitelist"] > button, [data-guide="complete-whitelist"] > .completed-badge',
          title: "Complete and lock your whitelist",
          text: completed
            ? "This step is complete. The whitelist settings are locked and completion cannot be undone."
            : "Check that all required apps and websites are included and that your changes have finished saving. You need at least one entry to continue. Click “Complete & Lock Whitelist” when you are ready. This locks the settings and cannot be undone.",
        },
      ].filter(({ target }) => {
        const element = document.querySelector(target);
        return (
          element &&
          getComputedStyle(element).visibility === "visible" &&
          element.getBoundingClientRect().width > 0 &&
          element.getBoundingClientRect().height > 0
        );
      });
      whitelistGuideStep = 0;
      showWhitelistGuide = whitelistGuideSteps.length > 0;
    }),
  );

  $: tableLoading = isWebApp ? webapploading : overviewloading;
  $: overviewRows = withShareValues(overview);
  $: webappRows = withShareValues(webapps);
  // Erkannte Eintraege kommen erst mit erhobenen Daten, vorher statt leerer Tabelle nur der Hinweis
  $: detectedLoading = isWebApp ? webapploading : overviewloading;
  // Ohne erhobene Daten gibt es nichts zu filtern, der Teamfilter entfaellt dann
  $: noDetectedEntries =
    !detectedLoading &&
    !(isWebApp ? originalWebapps : originalOverview).length;
  // Weder erkannte noch eingetragene Eintraege: ein zentraler Leerzustand statt Warnung und zwei leerer Tabellen
  let limitationList;
  let whitelistSaving = false;
  let whitelistCompleted = false;
  $: if (idproject)
    projectListStore
      .isStepCompleted(idproject, step, stage)
      .then((completed) => (whitelistCompleted = completed));
  // The page is only empty when neither apps nor websites were detected,
  // otherwise the tab without entries would hide the other one.
  $: noneDetected =
    !overviewloading &&
    !webapploading &&
    !originalOverview.length &&
    !originalWebapps.length;
  $: whitelistPageEmpty =
    noneDetected && !hasWhitelistEntries && !isCompleted;
  $: detectedEmptyText = (isWebApp ? webSearchValue : desktopSearchValue)?.trim()
    ? "No matching entries."
    : `No ${isWebApp ? "websites" : "apps"} detected for the selected teams.`;
  $: filteredOverviewRows = filterRows(overviewRows, desktopSearchValue, [
    "clearname",
    "application_name",
    "nteams",
  ]);
  $: filteredWebappRows = filterRows(webappRows, webSearchValue, [
    "clearname",
    "url",
    "nteams",
  ]);
  $: selectedTeamNames = (isWebApp ? teamsweb : teams)
    .filter((team) => teamids.includes(team.id))
    .map((team) => team.text);
  $: teamFilterLabel =
    selectedTeamNames.length > 1
      ? `Selected Teams: ${selectedTeamNames.join(", ")}`
      : selectedTeamNames.length === 1
        ? `Selected Team: ${selectedTeamNames[0]}`
        : "Select a team";

  $: ({ idproject } = $urlParams);

  $: ({ applicationLimitations } = $applicationLimitationStore);
  $: ({ idLimitation } = $projectListStore);
  $: whitelistApplicationCount = applicationLimitations.filter(
    (item) => !Boolean(item.isbrowser),
  ).length;
  $: whitelistWebsiteCount = applicationLimitations.filter((item) =>
    Boolean(item.isbrowser),
  ).length;
  $: hasWhitelistEntries = applicationLimitations.length > 0;
  // Entries are saved under their readable name, so match on the technical
  // name (apps) and the URL (websites) that are stored with them.
  $: whitelistedApps = new Set(
    applicationLimitations
      .filter((item) => !item.isbrowser)
      .map((item) => item.appname || item.name),
  );
  $: whitelistedUrls = new Set(
    applicationLimitations
      .filter((item) => item.isbrowser)
      .map((item) => item.url || item.name),
  );
  $: isOnWhitelist = (row, web) =>
    web ? whitelistedUrls.has(row.url) : whitelistedApps.has(row.application_name);

  $: if (idproject) {
    loadProjectData();
  }
  $: {
    if (applicationLimitations.length > 0 && firstTime) {
      selectedRowIds = getSelected(isWebApp);
      firstTime = false;
    }
  }

  $: {
    if (teamids.length > 0) {
      if (isWebApp) {
        webapps = originalWebapps.filter((webapp) =>
          webapp.teamlist.some((team) => teamids.includes(team.id)),
        );
      } else {
        overview = originalOverview.filter((app) =>
          app.teamlist.some((team) => teamids.includes(team.id)),
        );
      }
    } else {
      overview = [...originalOverview];
      webapps = [...originalWebapps];
    }
  }

  // Re-check entries that are on the whitelist when the whitelist, the tab or
  // the loaded rows really change. Not on every update, otherwise the user's
  // fresh selection would be reset right after clicking a row.
  let lastSelectionKey = "";
  $: selectionKey = [
    isWebApp,
    [...whitelistedApps].join(","),
    [...whitelistedUrls].join(","),
    overview.map((row) => row.id).join(","),
    webapps.map((row) => row.id).join(","),
  ].join("|");
  $: if (selectionKey !== lastSelectionKey) {
    lastSelectionKey = selectionKey;
    selectedRowIds = getSelected(isWebApp);
  }

  async function getAppOverview() {
    overviewloading = true;
    webapploading = true;
    const res = await fetchWithSession(
      `/api/damprojects/AppOverview?idproject=${idproject}&stage=SCOPING`,
    );
    const text = await res.json();
    const { appOverview, webAppOverview } = text;
    overview = appOverview;
    webapps = webAppOverview;
    originalOverview = [...overview];
    originalWebapps = [...webapps];
    teams = getTeamOptions(overview);
    teamsweb = getTeamOptions(webapps);
    overviewloading = false;
    webapploading = false;
    // Open the websites when Scoping detected only websites
    if (!originalOverview.length && originalWebapps.length) isWebApp = 1;
    selectedRowIds = getSelected(isWebApp);
    if (res.ok) {
      return text;
    } else {
      throw new Error(text);
    }
  }

  async function getProjectTeams() {
    try {
      const res = await fetchWithSession(
        `/api/damprojects/dam-teams?idproject=${idproject}`,
      );
      const projectTeams = await res.json();
      const teamOptions = projectTeams.map((team) => ({
        id: team.id,
        text: team.name,
      }));
      teams = teamOptions;
      teamsweb = teamOptions;
    } catch (err) {
      console.error("Error fetching project teams:", err);
    }
  }

  function getTeamOptions(apps) {
    const teamsById = new Map();
    apps.forEach((app) => {
      app.teamlist.forEach((team) => {
        if (!teamsById.has(team.id)) {
          teamsById.set(team.id, { id: team.id, text: team.name });
        }
      });
    });
    return Array.from(teamsById.values());
  }

  function withShareValues(rows) {
    const totalTime = rows.reduce((sum, row) => sum + Number(row.time || 0), 0);
    const totalKeystrokes = rows.reduce(
      (sum, row) => sum + Number(row.keystrokes || 0),
      0,
    );
    const totalMouseClicks = rows.reduce(
      (sum, row) => sum + Number(row.mouseclicks || 0),
      0,
    );

    return rows.map((row) => ({
      ...row,
      usageShare: getShareValue(row.time, totalTime),
      keystrokeShare: getShareValue(row.keystrokes, totalKeystrokes),
      mouseClickShare: getShareValue(row.mouseclicks, totalMouseClicks),
    }));
  }

  function getShareValue(value, total) {
    if (!total) return 0;
    return (Number(value || 0) / total) * 100;
  }

  function formatShare(value) {
    if (value > 0 && value < 1) return "<1%";
    return `${Math.round(value)}%`;
  }

  function formatNumber(value) {
    return Number(value || 0).toLocaleString();
  }

  function appIconUrl(row) {
    const application = isWebApp ? row.url : row.application_name;
    return application
      ? `/api/damprojects/app-icon?application=${encodeURIComponent(application)}`
      : "";
  }

  function filterRows(rows, searchValue, keys) {
    if (!searchValue) return rows;
    const query = searchValue.toLowerCase();
    return rows.filter((row) =>
      keys.some((key) => {
        const value = row[key];
        return value != null && String(value).toLowerCase().includes(query);
      }),
    );
  }

  function addWebApplications() {
    let newAppclications = [];
    webapps.forEach((webapp) => {
      if (selectedRowIds.includes(webapp.id) && !isOnWhitelist(webapp, true)) {
        newAppclications.push({
          id: 0,
          isbrowser: 1,
          name: webapp.clearname || webapp.url,
          appname: "",
          url: webapp.url,
          windowtitle: "",
        });
      }
    });
    addedApplications = newAppclications;
  }

  function addDesktopApplications() {
    let newAppclications = [];
    overview.forEach((app) => {
      if (selectedRowIds.includes(app.id) && !isOnWhitelist(app, false)) {
        newAppclications.push({
          id: 0,
          isbrowser: 0,
          name: app.clearname || app.application_name,
          appname: app.application_name,
          windowtitle: "",
          url: "",
        });
      }
    });
    addedApplications = newAppclications;
  }

  function getSelected(isWebApp) {
    const rows = isWebApp ? webapps : overview;
    return rows.filter((row) => isOnWhitelist(row, isWebApp)).map((row) => row.id);
  }

  async function loadProjectData() {
    await Promise.all([
      getProjectLimitations(idproject, stage),
      getProjectTeams(),
    ]);
    getAppOverview();
  }

  $$restProps;
</script>

<Header
  section=""
  area="Scoping"
  breadcrumb="Define Whitelist"
  tooltipText="Select the apps and websites to include in data collection during Discovery. &quot;Complete &amp; Lock Whitelist&quot; finishes this step. Afterwards, the whitelist cannot be changed."
>
  <!-- Eintraege werden direkt gespeichert, nach dem Lock entfaellt der Status -->
  {#if hasWhitelistEntries && !isCompleted && !whitelistCompleted}
    <SaveStatus saving={whitelistSaving} />
  {/if}
  <div data-guide="complete-whitelist">
    <ProjectStepActions
      {idproject}
      {step}
      {stage}
      disabled={!hasWhitelistEntries}
      showGoBackButton={false}
      bind:toastMsg
      bind:saveSuccessful
      text="Complete & Lock Whitelist"
      size="sm"
      completionWarning="After completion, the whitelist settings cannot be changed. Please make sure all required applications and websites are included before continuing. This action cannot be undone."
    />
  </div>
</Header>

<SectionWrapper
  fullscreen={true}
  seamless={true}
  seamlessPadding={true}
  loading={tableLoading}
  loadingTitle="Loading whitelist."
  loadingHint="Please wait while the applications and websites are prepared."
>
  <div class="main" slot="main" data-guide="whitelist-content">
    {#if whitelistPageEmpty}
      <div class="whitelist_empty_state">
        <span class="detected_empty_icon" aria-hidden="true"
          ><img src="/img/assistant/icons/info.svg" alt="" /></span
        >
        <EmptyStateText
          title="No apps or websites yet"
          hint="Apps and websites appear here once the teams start working with them. We recommend waiting for these, so the whitelist is based on real usage. You can also add entries manually."
        >
          <svelte:fragment slot="actions">
            <Button
              kind="secondary"
              size="field"
              text="Add App or Website Manually"
              on:click={() => limitationList?.openAddDialog()}
            />
          </svelte:fragment>
        </EmptyStateText>
      </div>
    {:else}
    <div class="content_wrapper" data-guide="detected-whitelist">
      <div data-guide="whitelist-overview">
        {#if !noDetectedEntries}
          <InfoBlock
            type="info"
            heading="Detected Apps & Websites"
            message="Select entries and add them to the whitelist below."
            showClose={false}
          />
        {/if}
      </div>
      {#if !noDetectedEntries}
        <div class="filter_bar">
          <div class="team_selector">
            {#key `${isWebApp}-${teams.length}-${teamsweb.length}`}
              <MultiSelect
                bind:selectedIds={teamids}
                label={teamFilterLabel}
                items={isWebApp ? teamsweb : teams}
              />
            {/key}
          </div>
        </div>
      {/if}

      {#if noDetectedEntries}
        {#if !noneDetected}
          <!-- The other tab has entries, the switcher must stay reachable -->
          <div class="toolbar_content_switcher detected_empty_switcher">
            <ContentSwitcherNew
              options={switcherOptions}
              bind:selected={isWebApp}
            />
          </div>
        {/if}
        <!-- Nur dieser Bereich ist leer, die Whitelist darunter bleibt bedienbar -->
        <div class="detected_empty_state">
          <span class="detected_empty_icon" aria-hidden="true"
            ><img src="/img/assistant/icons/info.svg" alt="" /></span
          >
          <div>
            <EmptyStateText
              title={`No ${isWebApp ? "websites" : "apps"} detected yet`}
              hint={`Detected ${isWebApp ? "websites" : "apps"} appear here once data has been collected in this project. You can still add entries to the whitelist manually below.`}
            />
          </div>
        </div>
      {:else if isWebApp}
        {#if webapploading}
          <div class="table_loading">Loading whitelist...</div>
        {:else}
          <DataTableBlack
            emptyText={detectedEmptyText}
            headers={[
              { key: "clearname", value: "Website" },
              { key: "url", value: "URL", width: "15rem" },
              {
                key: "usageShare",
                value: "Usage",
                width: "9rem",
                tooltip: shareHeaderTooltip,
              },
              {
                key: "keystrokeShare",
                value: "Keystrokes",
                width: "9rem",
                tooltip: shareHeaderTooltip,
              },
              {
                key: "mouseClickShare",
                value: "Mouse Clicks",
                width: "9rem",
                tooltip: shareHeaderTooltip,
              },
              { key: "nteams", value: "Teams", width: "6rem" },
            ]}
            rows={filteredWebappRows}
            sortable
            slicePage
            bind:sortKey
            bind:sortDirection
            batchSelection
            bind:selectedRowIds
            bind:page={webPage}
            bind:pageSize={webPageSize}
            pageSizes={[10]}
            newButtonText="Add selected to whitelist"
            newButtonIcon={null}
            newButtonIconLeft={false}
            searchValue={webSearchValue}
            on:addNew={() => {
              addWebApplications();
            }}
            on:search={(e) => {
              webSearchValue = e.detail.value;
            }}
            on:clearSearch={() => {
              webSearchValue = "";
            }}
            totalItems={filteredWebappRows.length}
          >
            <div slot="left-toolbar" class="toolbar_content_switcher">
              <ContentSwitcherNew
                options={switcherOptions}
                bind:selected={isWebApp}
              />
            </div>
            <svelte:fragment slot="cell-header" let:header>
              {#if header.tooltip}
                <div class="header_with_tooltip">
                  {header.value}
                  <Tooltip>{header.tooltip}</Tooltip>
                </div>
              {:else}
                {header.value}
              {/if}
            </svelte:fragment>
            <svelte:fragment slot="cell" let:cell let:row>
              {#if cell.key === "clearname"}
                <div class="display-name-cell">
                  <img
                    class="application-icon"
                    src={appIconUrl(row)}
                    alt=""
                    aria-hidden="true"
                  />
                  <span class="display-name">{cell.value}</span>
                  {#if isOnWhitelist(row, true)}
                    <Tag type="gray" size="sm" class="whitelist-chip">On whitelist</Tag>
                  {/if}
                </div>
              {:else if cell.key === "usageShare"}
                <span title={formatTimeDelta(row.time)}
                  >{formatShare(cell.value)}</span
                >
              {:else if cell.key === "keystrokeShare"}
                <span title={`${formatNumber(row.keystrokes)} keystrokes`}
                  >{formatShare(cell.value)}</span
                >
              {:else if cell.key === "mouseClickShare"}
                <span title={`${formatNumber(row.mouseclicks)} mouse clicks`}
                  >{formatShare(cell.value)}</span
                >
              {:else}
                {cell.value}
              {/if}
            </svelte:fragment>
          </DataTableBlack>
        {/if}
      {:else if overviewloading}
        <div class="table_loading">Loading whitelist...</div>
      {:else}
        <DataTableBlack
          emptyText={detectedEmptyText}
          headers={[
            { key: "clearname", value: "App" },
            { key: "application_name", value: "Technical Name", width: "11rem" },
            {
              key: "usageShare",
              value: "Usage",
              width: "9rem",
              tooltip: shareHeaderTooltip,
            },
            {
              key: "keystrokeShare",
              value: "Keystrokes",
              width: "9rem",
              tooltip: shareHeaderTooltip,
            },
            {
              key: "mouseClickShare",
              value: "Mouse Clicks",
              width: "9rem",
              tooltip: shareHeaderTooltip,
            },
            { key: "nteams", value: "Teams", width: "6rem" },
          ]}
          rows={filteredOverviewRows}
          sortable
          slicePage
          bind:sortKey
          bind:sortDirection
          batchSelection
          bind:selectedRowIds
          bind:page={appsPage}
          bind:pageSize={appsPageSize}
          pageSizes={[10]}
          newButtonText="Add selected to whitelist"
            newButtonIcon={null}
          newButtonIconLeft={false}
          searchValue={desktopSearchValue}
          on:addNew={() => {
            addDesktopApplications();
          }}
          on:search={(e) => {
            desktopSearchValue = e.detail.value;
          }}
          on:clearSearch={() => {
            desktopSearchValue = "";
          }}
          totalItems={filteredOverviewRows.length}
        >
          <div slot="left-toolbar" class="toolbar_content_switcher">
            <ContentSwitcherNew
              options={switcherOptions}
              bind:selected={isWebApp}
            />
          </div>
          <svelte:fragment slot="cell-header" let:header>
            {#if header.tooltip}
              <div class="header_with_tooltip">
                {header.value}
                <Tooltip>{header.tooltip}</Tooltip>
              </div>
            {:else}
              {header.value}
            {/if}
          </svelte:fragment>
          <svelte:fragment slot="cell" let:cell let:row>
            {#if cell.key === "clearname"}
              <div class="display-name-cell">
                <img
                  class="application-icon"
                  src={appIconUrl(row)}
                  alt=""
                  aria-hidden="true"
                />
                <span class="display-name">{cell.value}</span>
                {#if isOnWhitelist(row, false)}
                  <Tag type="gray" size="sm" class="whitelist-chip">On whitelist</Tag>
                {/if}
              </div>
            {:else if cell.key === "usageShare"}
              <span title={formatTimeDelta(row.time)}
                >{formatShare(cell.value)}</span
              >
            {:else if cell.key === "keystrokeShare"}
              <span title={`${formatNumber(row.keystrokes)} keystrokes`}
                >{formatShare(cell.value)}</span
              >
            {:else if cell.key === "mouseClickShare"}
              <span title={`${formatNumber(row.mouseclicks)} mouse clicks`}
                >{formatShare(cell.value)}</span
              >
            {:else}
              {cell.value}
            {/if}
          </svelte:fragment>
        </DataTableBlack>
      {/if}
    </div>

    <div class="whitelist-summary">
      <InfoBlock
        type={hasWhitelistEntries ? "hint" : "warning"}
        heading={`Selected for Discovery · ${whitelistApplicationCount} apps, ${whitelistWebsiteCount} websites`}
        message={hasWhitelistEntries
          ? "These apps and websites are included in data collection during Discovery."
          : "No application or website is currently included in the whitelist. No data would be collected until at least one is added."}
        showClose={false}
      />
    </div>
    {/if}

    <div class="whitelist_entries" data-guide="whitelist-entries">
      <LimitationList
        bind:this={limitationList}
        editable={!isCompleted}
        showTypeSelector={isAdmin && !isCompleted}
        showAddIcon={false}
        showTable={!whitelistPageEmpty}
        bind:saving={whitelistSaving}
        bind:addedApplications
      />
    </div>
  </div>
</SectionWrapper>

<SpotlightGuide
  bind:open={showWhitelistGuide}
  bind:currentStep={whitelistGuideStep}
  steps={whitelistGuideSteps}
  allowInteraction={false}
/>

{#if toastMsg}
  <div
    in:fly={{ x: 100 }}
    out:fly={{ y: 100 }}
    style="position:fixed; top:60px; right:20px; z-index:100;"
  >
    <ToastNotification
      fullWidth
      hideAfter={saveSuccessful ? 3000 : null}
      timeout={saveSuccessful ? 3000 : null}
      kind={saveSuccessful ? "success" : "error"}
      title={saveSuccessful ? "Success" : "Error"}
      subtitle={toastMsg}
      caption={new Date().toLocaleString()}
      on:close={(e) => {
        e.preventDefault();
        toastMsg = null;
        saveSuccessful = false;
      }}
    />
  </div>
{/if}

<style>
  /* Centered in the page like the SectionWrapper empty state */
  .whitelist_empty_state {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    min-height: 60vh;
  }

  .detected_empty_state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 3rem 1rem;
  }

  /* Same icon as the SectionWrapper empty state */
  .detected_empty_icon {
    display: flex;
    flex: 0 0 3rem;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    background: var(--pax-blue);
  }

  .detected_empty_icon img {
    display: block;
    width: 1.5rem;
    height: 1.5rem;
    filter: brightness(0);
  }

  .main {
    max-width: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .content_wrapper,
  .whitelist_entries {
    display: flex;
    flex-direction: column;
    row-gap: var(--control-gap);
  }

  /* Pagination schliesst direkt an die Tabelle an */
  .content_wrapper > :global(.bx--pagination),
  .whitelist_entries > :global(.bx--pagination) {
    margin-top: calc(-1 * var(--control-gap));
  }

  .filter_bar {
    display: flex;
    align-items: flex-end;
    gap: var(--control-gap);
    margin-bottom: 0;
  }

  .team_selector {
    width: 100%;
  }

  .toolbar_content_switcher {
    flex: 0 0 auto;
  }

  .detected_empty_switcher {
    width: fit-content;
  }

  .table_loading {
    display: flex;
    align-items: center;
    min-height: 4rem;
    padding: 0 1rem;
    color: #6f7681;
    background-color: var(--pax-white);
    border: 1px solid #dfe3e6;
    border-radius: 0.5rem;
  }

  .table_loading {
    min-height: 12rem;
  }

  .header_with_tooltip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  .display-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Quiet hint next to the name, colors from app.css */
  .display-name-cell :global(.whitelist-chip.bx--tag) {
    flex: 0 0 auto;
    margin: 0 0 0 0.5rem;
    background-color: var(--pax-smoke);
    color: var(--pax-grey);
    font-size: 0.6875rem;
  }

  .display-name-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }

  .application-icon {
    width: 1.5rem;
    height: 1.5rem;
    flex: 0 0 1.5rem;
    object-fit: contain;
    border-radius: 0.25rem;
  }

  .whitelist-summary {
    display: grid;
    gap: 1rem;
    align-items: stretch;
  }

  .whitelist-summary {
    grid-template-columns: minmax(0, 1fr);
  }

  @media (max-width: 700px) {
    .whitelist-summary {
      grid-template-columns: 1fr;
    }

    .filter_bar {
      align-items: stretch;
      flex-direction: column;
    }

    .team_selector {
      width: 100%;
    }
  }
</style>
